import { enqueueSnackbar } from 'notistack';
import { useState, Activity, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { IconTrash, IconPencil } from '@tabler/icons-react';

import ConfirmationModal from '@/components/ConfirmationModal';
import SectionLoader from '@/components/Loader/SectionLoader';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { deletePolicy, getPolicyById } from '@/service/editorialService';
import { IPolicyData } from '@/types';
import { IDeletePolicyPayload } from '@/types/payload';
import { formatUnixToLongDate } from '@/utils';

const ViewPolicy = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatText } = useFormatText();
  const { setTitle, setIsBreadcrumb } = usePageTitle();

  const [loading, setLoading] = useState(true);
  const [policyData, setPolicyData] = useState<IPolicyData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const policyName = location.state?.policyName || '';

  const title = formatText('editorial.title');
  const breadcrumbLabel =
    policyName || formatText('editorial.policy.view.title');
  const pageTitle = formatText('editorial.policy.view.title');

  // Set page title and breadcrumb
  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([
      {
        label: title,
        to: '/editorial-management?tab=policy',
      },
      {
        label: breadcrumbLabel,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbLabel, pageTitle]);

  // Fetch policy data
  useEffect(() => {
    const fetchPolicyData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await getPolicyById(id);
        if (response?.status && response?.data) {
          setPolicyData(response.data);
        }
      } catch (error) {
        console.error('Error fetching policy:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicyData();
  }, [id]);

  const handleEdit = () => {
    navigate(`/editorial-management/policy/edit/${id}`, {
      state: { policyName: policyData?.policy_name },
    });
  };

  const handleConfirmDelete = async () => {
    if (!id) return;
    setDeleteLoading(true);

    try {
      const payload: IDeletePolicyPayload = {
        policy_id: id,
      };
      const response = await deletePolicy(payload);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        navigate('/editorial-management?tab=policy');
      } else {
        console.error('Error deleting policy:', response?.message);
      }
    } catch (error) {
      console.error('Error deleting policy:', error);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <div className="w-full">
      {loading ? (
        <SectionLoader className="h-[50vh] flex justify-center" />
      ) : policyData ? (
        <>
          <div className="w-full bg-white rounded-xl border border-neutral-light-200 flex flex-col max-h-[calc(100vh-180px)]">
            {/* Header */}
            <div className="flex items-start justify-between p-6 pb-4 border-b border-neutral-light-200 shrink-0">
              <div>
                <h1 className="text-xl font-semibold text-neutral-primary">
                  {policyData.policy_name}
                </h1>
                <p className="text-sm text-neutral-secondary mt-1">
                  {formatText('editorial.policy.view.last-updated')}{' '}
                  {formatUnixToLongDate(Number(policyData.last_modify_at))}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="p-2.5 rounded-xl border border-neutral-light-200 hover:bg-neutral-light-100 cursor-pointer transition-colors"
                  title={formatText('common.actions.edit')}
                  onClick={handleEdit}
                >
                  <IconPencil size={20} className="text-neutral-hover" />
                </button>
                <button
                  className="p-2.5 rounded-xl border border-neutral-light-200 hover:bg-red-100 cursor-pointer transition-colors"
                  title={formatText(
                    'editorial.policy.actions.delete-policy-button'
                  )}
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  <IconTrash size={20} className="text-red-500" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div
              className="p-6 max-w-none text-neutral-primary policy-view-content overflow-y-auto"
              dangerouslySetInnerHTML={{
                __html: policyData.description || '',
              }}
            />
          </div>

          <Activity mode={isDeleteModalOpen ? 'visible' : 'hidden'}>
            <ConfirmationModal
              isOpen={isDeleteModalOpen}
              setIsOpen={setIsDeleteModalOpen}
              title="editorial.policy.actions.delete-policy-title"
              descriptionPrefix="editorial.policy.actions.delete-policy-prefix"
              descriptionSuffix="editorial.policy.actions.delete-policy-suffix"
              entityName={policyData.policy_name}
              confirmLabel={formatText(
                'editorial.policy.actions.delete-policy-button'
              )}
              onConfirm={handleConfirmDelete}
              isLoading={deleteLoading}
              isDestructive
            />
          </Activity>
        </>
      ) : null}
    </div>
  );
};

export default ViewPolicy;
